import { rd, type RemoteData } from '@passionware/monads';

/** One page of a continuous list, as a page read returns it. */
export interface ListPage<T> {
  items: T[];
  /** All the list holds, every page together. */
  count: number;
}

/** What stands at a place of the list: its item, or why not yet. */
export type ListSlot<T> =
  | { type: 'item'; item: T }
  | { type: 'loading' }
  | { type: 'error' };

/**
 * A list shown as one continuous stretch while read page by page: which pages the rows on
 * screen fall on, and what stands at each place meanwhile.
 */
export const pagedList = {
  /** The pages the given places fall on, each once, in order. */
  pagesOf: (indices: Iterable<number>, pageSize: number): number[] =>
    [...new Set([...indices].map(index => Math.floor(index / pageSize)))].sort(
      (a, b) => a - b
    ),

  /** How long the list is, from whichever page has come; `null` before any. */
  countOf: <T>(pages: Iterable<RemoteData<ListPage<T>>>): number | null => {
    for (const page of pages) {
      const count = rd.tryMap(page, loaded => loaded.count);
      if (count !== undefined) return count;
    }
    return null;
  },

  slotAt: <T>(
    index: number,
    pageSize: number,
    pages: ReadonlyMap<number, RemoteData<ListPage<T>>>
  ): ListSlot<T> => {
    const page = pages.get(Math.floor(index / pageSize));
    if (page === undefined || rd.isPending(page) || rd.isIdle(page)) {
      return { type: 'loading' };
    }
    if (rd.isError(page)) return { type: 'error' };
    const item = rd.getOrThrow(page).items[index % pageSize];
    // a page shorter than the count promised: the list shrank since
    return item === undefined ? { type: 'loading' } : { type: 'item', item };
  }
};
